import React, { useEffect, useState } from "react";
import { Modal, Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import "./ProductModal.scss";
import { PublicProductDetails, SizeDetail } from "interfaces/IProduct";
import { IReviewDetailResponse } from "interfaces/IReviews";
import {
  createAuthenticatedAxiosInstance,
  fetchPublicProductById,
  toggleFavorite,
  getProductReviews,
} from "api/auth";
import LoadingScreen from "components/general/LoadingScreen";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { faHeart as faHeartSolid } from "@fortawesome/free-solid-svg-icons";
import { useAuthStore } from "../../../../zustand/loginStore";
import { useCartStore } from "../../../../zustand/cartStore";
import CustomToast from "components/general/CustomToast";
import { useUserStore } from "../../../../zustand/userStore";

interface ProductModalProps {
  show: boolean;
  onHide: () => void;
  productId: number;
}

const ProductModal: React.FC<ProductModalProps> = ({
  show,
  onHide,
  productId,
}) => {
  const { favoriteProducts, setFavoriteProducts } = useUserStore();
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<SizeDetail>();
  const [selectedSizeId, setSelectedSizeId] = useState<number>(0);
  const [priceInfo, setPriceInfo] = useState<JSX.Element>(<></>);
  const [productDetails, setProductDetails] =
    useState<PublicProductDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [heartDisabled, setHeartDisabled] = useState<boolean>(true);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipOpacity, setTooltipOpacity] = useState(1);
  const [showToast, setShowToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "¡Añadido al carrito!", show: false, success: true });
  const addToCart = useCartStore((state) => state.addItem);
  const { token } = useAuthStore();
  const [startIndex, setStartIndex] = useState(0);
  const [reviews, setReviews] = useState<IReviewDetailResponse[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const [isFavorite, setIsFavorite] = useState(false);

  const maxVisibleThumbnails = 4;
  const totalImages =
    productDetails?.colors[selectedColorIndex]?.images?.length ?? 0;
  const remainingImagesBottom =
    totalImages - (startIndex + maxVisibleThumbnails);
  const remainingImagesTop = startIndex;

  const handleScrollUp = () => {
    setStartIndex((prev) => Math.max(0, prev - 1));
  };

  const handleScrollDown = () => {
    setStartIndex((prev) =>
      Math.min(totalImages - maxVisibleThumbnails, prev + 1)
    );
  };

  // Función para renderizar las estrellas
  const renderStars = (rating: number) => {
    return (
      <div className="product-stars">
        {[1, 2, 3, 4, 5].map((star) => {
          const starValue = rating - (star - 1);
          let starClass = "product-star";

          if (starValue >= 1) {
            starClass += " active";
          } else if (starValue > 0) {
            starClass += " half-active";
          }

          return (
            <span key={star} className={starClass}>
              ★
            </span>
          );
        })}
      </div>
    );
  };

  /**
   * modular component to show discount quantities in case the product has discount
   */
  const handleSizeChange = (product?: SizeDetail) => {
    if (!product) return <></>;
    const hasDiscount = !!product.discount_type;
    const isPercentOff = product.discount_type.includes("%");

    const price = (
      <>
        {hasDiscount ? (
          <div className="d-flex gap-2">
            <span className="price">${product.new_price || 0}</span>
            {isPercentOff ? (
              <span className="discount-quantity-details">
                -{Math.trunc(product.discount)}
                {isPercentOff && "%"}
              </span>
            ) : (
              <span className="discount-quantity-details">
                - ${product.discount}
              </span>
            )}
            <span className="before-discount">${product.price || 0}</span>
          </div>
        ) : (
          <span className="price">${product.price || 0}</span>
        )}
      </>
    );
    setSelectedSize(product);
    setPriceInfo(price);
  };

  useEffect(() => {
    const loadProductDetails = async () => {
      if (show && productId) {
        try {
          const details = await fetchPublicProductById(productId);
          setProductDetails(details);
          // Set default selected size to first size of first color
          if (details.colors[0]?.sizes[0]?.size_id) {
            setSelectedSizeId(details.colors[0].sizes[0].size_id);
            handleSizeChange(details.colors[0].sizes[0]);
          }
        } catch (error) {
          console.error("Error loading product details:", error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadProductDetails();
  }, [show, productId]);

  useEffect(() => {
    const loadReviews = async () => {
      if (show && productId) {
        // Limpiar reviews anteriores
        setReviews([]);
        setAverageRating(0);
        setTotalReviews(0);

        try {
          const reviewsData = await getProductReviews(productId);
          setReviews(reviewsData);

          if (reviewsData.length > 0) {
            const totalRating = reviewsData.reduce(
              (sum, review) => sum + review.qualification,
              0
            );
            const average = totalRating / reviewsData.length;
            setAverageRating(average);
            setTotalReviews(reviewsData.length);
          }
        } catch (error) {
          console.error("Error loading reviews:", error);
        }
      }
    };

    loadReviews();
  }, [show, productId]);

  /**
   * use effect used to change the price element
   * [selectedSizeId] The current selected size id
   */
  useEffect(() => {
    const selection = productDetails?.colors[selectedColorIndex].sizes.find(
      (x) => selectedSizeId === x.size_id
    );
    handleSizeChange(selection);
  }, [selectedSizeId, selectedColorIndex]);

  /**
   * effect for favorites to find them in the store
   */
  useEffect(() => {
    setIsFavorite(false);
    favoriteProducts.forEach((fav) => {
      if (productId === fav.product_id) {
        setIsFavorite(true);
        return;
      }
    });
  }, [favoriteProducts, productId]);

  if (isLoading || !productDetails) {
    return <LoadingScreen loadingText="Cargando producto..." />;
  }

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(true);
    setTooltipOpacity(1);

    setTimeout(() => {
      setTooltipOpacity(0);
      setTimeout(() => {
        setShowTooltip(false);
      }, 1000);
    }, 1500);
  };
  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setHeartDisabled(true);
    if (token) {
      const { data, success } = await toggleFavorite(axiosInstance, productId);
      if (!success) {
        setShowToast({
          message: `El producto no pudo ser ${
            isFavorite ? "removido de" : "agregado a"
          } a la lista de favoritos, por favor intente nuevamente.`,
          show: true,
          success: false,
        });
        setHeartDisabled(false);
        return;
      }
      data && setFavoriteProducts(data);
      setShowToast({
        message: `El producto fue ${
          isFavorite ? "removido de" : "agregado a"
        } la lista de favoritos.`,
        show: true,
        success: true,
      });
    }
    setHeartDisabled(false);
  };
  const handleColorChange = (index: number) => {
    setSelectedColorIndex(index);
    setSelectedImageIndex(0);
    // Set first available size for the new color
    if (productDetails?.colors[index]?.sizes[0]?.size_id) {
      setSelectedSize(productDetails.colors[index].sizes[0]);
    }
  };

  const handleImageChange = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handlePrevImage = () => {
    setSelectedImageIndex((prev) =>
      prev === 0
        ? productDetails.colors[selectedColorIndex].images.length - 1
        : prev - 1
    );
  };

  const handleNextImage = () => {
    setSelectedImageIndex((prev) =>
      prev === productDetails.colors[selectedColorIndex].images.length - 1
        ? 0
        : prev + 1
    );
  };

  const handleAddToCart = () => {
    if (selectedSize) {
      const result = addToCart(
        productDetails,
        selectedColorIndex,
        selectedSize.size_id,
        selectedSize.id
      );
      
      if (result.success) {
        setShowToast({ 
          message: "¡Añadido al carrito!", 
          show: true, 
          success: true 
        });
        onHide();
      } else {
        setShowToast({
          message: result.message || "Error al agregar al carrito",
          show: true,
          success: false,
        });
      }
    }
  };

  const favoriteTooltip = (
    <Tooltip
      id="favorite-tooltip"
      className="custom-tooltip-client"
      style={{
        opacity: tooltipOpacity,
        lineHeight: "1.3",
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      Inicia sesión o crea
      <br />
      una cuenta para
      <br />
      guardar tus artículos
      <br />
      favoritos!
    </Tooltip>
  );

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="lg"
        className="product-modal"
      >
        <Modal.Body>
          <Row>
            <Col lg={1} className="thumbnail-column">
              {remainingImagesTop > 1 && (
                <button className="scroll-button" onClick={handleScrollUp}>
                  <FontAwesomeIcon icon={faChevronUp} />
                </button>
              )}

              {productDetails.colors[selectedColorIndex].images
                .slice(
                  remainingImagesTop === 1 ? startIndex - 1 : startIndex,
                  startIndex +
                    maxVisibleThumbnails +
                    (remainingImagesBottom === 1 ? 1 : 0)
                )
                .map((img, index) => (
                  <div
                    key={startIndex + index}
                    className={`thumbnail-wrapper ${
                      selectedImageIndex === startIndex + index
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => setSelectedImageIndex(startIndex + index)}
                  >
                    <img
                      src={img}
                      alt={`${productDetails.name}-${startIndex + index}`}
                    />
                  </div>
                ))}

              {remainingImagesBottom > 1 && (
                <button className="scroll-button" onClick={handleScrollDown}>
                  <FontAwesomeIcon icon={faChevronDown} />
                </button>
              )}
            </Col>

            <Col lg={5} className="main-image-column">
              <div className="main-image-wrapper">
                <div className="image-background">
                  <img
                    src={
                      productDetails.colors[selectedColorIndex].images[
                        selectedImageIndex
                      ]
                    }
                    alt={productDetails.name}
                  />
                </div>
                <button className="nav-button prev" onClick={handlePrevImage}>
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </button>
                <button className="nav-button next" onClick={handleNextImage}>
                  <FontAwesomeIcon icon={faAnglesRight} />
                </button>
              </div>
            </Col>

            <Col lg={5} className="product-details">
              <h2>{productDetails.name}</h2>
              {/* Calificaciones del producto */}
              {totalReviews > 0 && (
                <div className="product-rating-section">
                  {renderStars(averageRating)}
                  <span className="rating-text">
                    (
                    {totalReviews === 1
                      ? "1 calificación"
                      : `${totalReviews} calificaciones`}
                    )
                  </span>
                </div>
              )}
              {priceInfo}
              <hr />

              <div className="colors-section">
                <label>
                  Estilos disponibles:{" "}
                  {productDetails.colors[selectedColorIndex].colorName}
                </label>
                <div className="color-options">
                  {productDetails.colors.map((color, index) => (
                    <button
                      key={index}
                      className={`color-circle ${
                        selectedColorIndex === index ? "selected" : ""
                      }`}
                      style={{ backgroundColor: color.color }}
                      onClick={() => handleColorChange(index)}
                    />
                  ))}
                </div>
              </div>

              <div className="sizes-section">
                <label>Tallas:</label>
                <div className="size-options">
                  {productDetails.colors[selectedColorIndex].sizes.map(
                    (size) => (
                      <button
                        key={size.size_id}
                        className={`size-button ${
                          selectedSizeId === size.size_id ? "selected" : ""
                        }`}
                        onClick={() => setSelectedSizeId(size.size_id)}
                      >
                        {size.name}
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="actions">
                {token ? (
                  <button className="like-button" onClick={handleFavoriteClick}>
                    <FontAwesomeIcon
                      icon={isFavorite ? faHeartSolid : faHeartRegular}
                    />
                  </button>
                ) : (
                  <OverlayTrigger
                    show={showTooltip}
                    trigger="click"
                    placement="top"
                    overlay={favoriteTooltip}
                    rootClose
                  >
                    <button className="like-button" onClick={handleHeartClick}>
                      <FontAwesomeIcon
                        icon={faHeartRegular}
                        style={{ height: "40px" }}
                      />
                    </button>
                  </OverlayTrigger>
                )}
                <button
                  className="add-to-cart"
                  onClick={handleAddToCart}
                  disabled={!selectedSize}
                >
                  Añadir al carrito
                </button>
              </div>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      {/* Add CustomToast component */}
      <CustomToast
        show={showToast.show}
        onClose={() =>
          setShowToast({
            message: "¡Añadido al carrito!",
            show: false,
            success: true,
          })
        }
        message={showToast.message}
        width="220px"
        success={showToast.success}
      />
    </>
  );
};

export default ProductModal;
